import { FC, MutableRefObject, Suspense, useEffect, useRef, useState } from 'react';
interface Props {
  filters: ProgressTableClassFilterState;
  totalRecords: number;
  hideButton?: number | null;
}
const initialPageControls: PageControls = {
  first: 0,
  rows: 5,
  page: 0,
  pageCount: 0,
  prevPage: 0,
  totalPages: 0,
};

const PAGE_SIZE = 50;
export const ProgressSkillCsvButton: FC<Props> = ({ filters, totalRecords, hideButton = null }) => {
  const [fetchData, setFetchData] = useState(false);
  const [localFilters, setLocalFilters] = useState({ ...filters });
  const [skillsFiltered, setSkillsFiltered] = useState<ProgressTableSkillsValue[]>([]);
  const [pageInfo, setPageInfo] = useState<PageInfo | undefined>(undefined);
  const [finishedLoading, setFinishedLoading] = useState(false);
  const [pageControls, setPageControls] = useState(initialPageControls);
  const dispatch = useAppDispatch();

  const totalOfQuerys = Math.ceil(totalRecords / PAGE_SIZE);
  const [queryNumber, setQueryNumber] = useState<number>(1);
  const [progressValueToast, setProgressValueToast] = useState<number>(0);
  const interval = useRef<number | null>(null);
  const downloadToastRef = useRef<Toast | null>(null);
  const successfullToastRef = useRef<Toast | null>(null);

  const clear = async () => {
    setTimeout(() => {
      setSkillsFiltered([]);
      setFetchData(false);
      setFinishedLoading(false);
      setPageInfo(undefined);
      clearDownloadToast(setQueryNumber, setProgressValueToast, interval, downloadToastRef);
    }, 500);
  };

  useEffect(() => {
    setLocalFilters(filters);
    setPageControls({ ...pageControls, page: 0, rows: PAGE_SIZE });
  }, [filters]);

  useEffect(() => {
    if (skillsFiltered.length) {
      exportToCsvProgressTable(skillsFiltered, ProgressType.SKILLS)
        .then(() => {
          clear().then(() => {
            showSuccessfullToast(successfullToastRef);
            setTimeout(() => {
              dispatch(setCsvProcess(false));
              dispatch(setReportHubQuerysInProgress(false));
            }, 3000);
          });
        })
        .catch(() => {
          dispatch(setCsvProcess(false));
          dispatch(setReportHubQuerysInProgress(false));
        });
    }
  }, [skillsFiltered.length]);

  useEffect(() => {
    if (fetchData && !pageInfo?.hasNextPage) {
      setFinishedLoading(true);
    }
  }, [pageInfo]);

  useEffect(() => {
    if (hideButton) {
      dispatch(setCsvProcess(true));
      setFetchData(true);
      showDownloadToast(downloadToastRef);
    }
  }, [hideButton]);

  return (
    <>
      {!hideButton && (
        <Button
          icon="nc_icon-nc_export icon-sm"
          label="Export Table as CSV"
          tooltip="Export the filtered data to CSV format"
          aria-label="Export the filtered data to CSV format"
          iconPos="right"
          rounded
          outlined
          disabled={fetchData}
          onClick={() => {
            dispatch(setCsvProcess(true));
            setFetchData(true);
            showDownloadToast(downloadToastRef);
          }}
        ></Button>
      )}
      {fetchData && (
        <Suspense>
          <ProgressSkillQueryDataCsv
            pageControls={pageControls}
            finishedLoading={finishedLoading}
            fetchData={fetchData}
            filterState={localFilters}
            setSkillsFiltered={setSkillsFiltered}
            pageInfo={pageInfo}
            setPageInfo={setPageInfo}
            setProgressValueToast={setProgressValueToast}
            totalOfQuerys={totalOfQuerys}
            interval={interval}
            queryNumber={queryNumber}
            setQueryNumber={setQueryNumber}
          />
        </Suspense>
      )}
      <ToastExportXLSorCSV downloadToastRef={downloadToastRef} clear={clear} progressValueToast={progressValueToast} />
      <ToastSuccessful successfullToastRef={successfullToastRef} />
    </>
  );
};

import { Dispatch, SetStateAction } from 'react';
import { useLazyLoadQuery } from 'react-relay';
import { PageInfo } from '../../models/PageInfo';
import { Button } from 'primereact/button';
import { ProgressTableClassFilterState } from './ProgressTableClass';
import { ProgressTableSkillsValue } from './QuerySkillsProgressReport';
import { exportToCsvProgressTable } from '../../businessLogicService/exportCsvProgressTable';
import { ProgressCsvSkillCsvQuery } from '../../graphQLOperations/__generated__/ProgressCsvSkillCsvQuery.graphql';
import { progressCsvSkillQuery, progressSkillsResultsReport } from '../../graphQLOperations/ProgressCsv';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { selectedReportsFilter } from '../../redux/reducers/filterReportsSlice';
import { ProgressCsvResultsReportQuery } from '../../graphQLOperations/__generated__/ProgressCsvResultsReportQuery.graphql';
import { getSkillTypeName } from '../../businessLogicService/interpreterValuesProgressTable';
import { setCsvProcess } from '../../redux/reducers/csvProcessSlice';
import { PageControls } from '../../models/PageControls';
import {
  clearDownloadToast,
  showDownloadToast,
  showSuccessfullToast,
} from '../../businessLogicService/exportToastHandler';
import { Toast } from 'primereact/toast';
import ToastExportXLSorCSV from '../ExportCSV/ToastExportXLSorCSV';
import ToastSuccessful from '../ExportCSV/ToastSuccessful';
import { ProgressType } from '../../models/CsvExport';
import { setReportHubQuerysInProgress } from '../../redux/reducers/reportHubQuerysInProgress';

export interface QuerySkillDataProp {
  fetchData: boolean;
  filterState: ProgressTableClassFilterState;
  pageControls: PageControls;
  setSkillsFiltered: Dispatch<SetStateAction<ProgressTableSkillsValue[]>>;
  setPageInfo: Dispatch<SetStateAction<PageInfo | undefined>>;
  pageInfo: PageInfo | undefined;
  finishedLoading: boolean;
  setProgressValueToast: (p: number) => void;
  totalOfQuerys: number;
  interval: MutableRefObject<number | null | undefined>;
  queryNumber: number;
  setQueryNumber: Dispatch<SetStateAction<number>>;
}

export const ProgressSkillQueryDataCsv = ({
  setSkillsFiltered,
  filterState,
  fetchData,
  setPageInfo,
  pageInfo,
  finishedLoading,
  pageControls,
  setProgressValueToast,
  totalOfQuerys,
  interval,
  queryNumber,
  setQueryNumber,
}: QuerySkillDataProp) => {
  const regex = /^\d+$/;
  const [temporaryValues, setTemporaryValues] = useState<ProgressTableSkillsValue[]>([]);
  const reportsFilterRedux = useAppSelector(selectedReportsFilter);
  const { allSkills } = useLazyLoadQuery<ProgressCsvSkillCsvQuery>(
    progressCsvSkillQuery,
    {
      first: pageControls.rows,
      after: pageInfo?.endCursor,
      filter: {
        targetGroupIds: reportsFilterRedux?.roadmaps?.id ? [reportsFilterRedux?.roadmaps?.id] : [],
        standardGradeIds: reportsFilterRedux?.skillGrades?.length
          ? reportsFilterRedux?.skillGrades?.map(skill => skill?.code)
          : undefined,
        skillName: regex.test(filterState?.filters?.name) ? null : filterState?.filters?.name,
        skillCode: regex.test(filterState?.filters?.name) ? filterState?.filters?.name : null,
        skillType: filterState?.filters?.skillType !== null ? filterState?.filters?.skillType?.id : null,
      },
      targetGroupsIds: reportsFilterRedux?.roadmaps?.id ? [reportsFilterRedux?.roadmaps?.id] : [],
    },
    { fetchKey: JSON.stringify(pageControls), fetchPolicy: 'network-only' },
  );

  const { skillsProgressReport } = useLazyLoadQuery<ProgressCsvResultsReportQuery>(
    progressSkillsResultsReport,
    {
      filter: {
        skillIds: temporaryValues.map(val => val.skillId),
        gradeLevelIds: reportsFilterRedux?.grades?.length
          ? reportsFilterRedux?.grades?.map(grade => grade?.id)
          : undefined,
        classIds: reportsFilterRedux?.classes?.map(cl => cl?.id),
      },
      skip: !temporaryValues.length || !finishedLoading,
    },
    { fetchPolicy: 'network-only', fetchKey: JSON.stringify(temporaryValues) },
  );

  useEffect(() => {
    if (finishedLoading) {
      const skilllsForCsv = temporaryValues.map(skill => {
        const skillResults = skillsProgressReport?.find(sk => sk.skill?.id === skill.skillId);

        return {
          ...skill,
          skillsResults: skillResults?.skillsResults ?? [],
        };
      });
      setSkillsFiltered([...skilllsForCsv] as ProgressTableSkillsValue[]);
      setTemporaryValues([]);
    }
  }, [skillsProgressReport]);

  useEffect(() => {
    if (queryNumber <= totalOfQuerys) {
      const calc = Math.round((queryNumber / totalOfQuerys) * 100);
      interval.current = calc;
      setProgressValueToast(calc);
      setQueryNumber((e: number) => e + 1);
    }
    if (allSkills?.nodes?.length && fetchData) {
      const currentValues =
        allSkills?.nodes?.map(
          val =>
            ({
              skillId: val?.id ?? '',
              skillName: val?.name ?? '',
              skillType: getSkillTypeName(
                val?.targetSkills
                  ?.filter(target => target?.target?.targetGroup?.id === reportsFilterRedux?.roadmaps?.id)
                  ?.map(targetSkill => targetSkill?.targetSkillType?.name ?? ''),
              ),
              code: val?.code ?? '',
              skillsResults: [],
            }) as ProgressTableSkillsValue,
        ) ?? [];
      setTemporaryValues([...temporaryValues, ...currentValues]);
      setPageInfo(allSkills?.pageInfo);
    }
  }, [allSkills]);

  return <div style={{ display: 'none' }}></div>;
};
